import { useEffect, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';

interface ImagePreviewProps {
  src?: string;
  alt?: string;
  /** The thumbnail to click (e.g. an Avatar). */
  children: ReactNode;
}

/** Wraps a thumbnail; clicking it opens the image large in a full-screen preview. */
export function ImagePreview({ src, alt = 'Preview', children }: ImagePreviewProps) {
  const [open, setOpen] = useState(false);
  const url = src?.trim();

  useEffect(() => {
    if (!open) return;
    // Listen on window in capture phase so Esc closes only the preview, not the drawer behind it.
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      setOpen(false);
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [open]);

  // No photo: render the thumbnail as-is, nothing to preview.
  if (!url) return <>{children}</>;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="shrink-0 cursor-zoom-in rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-[#4369a0]"
        aria-label={`View ${alt}`}
      >
        {children}
      </button>

      {open &&
        createPortal(
          <div
            // pointer-events-auto + stopPropagation keep it clickable when opened from inside a drawer.
            className="pointer-events-auto fixed inset-0 z-[9999] flex items-center justify-center bg-black/80 p-4"
            onPointerDown={(e) => e.stopPropagation()}
            onClick={() => setOpen(false)}
            role="dialog"
            aria-modal="true"
            aria-label={alt}
          >
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="absolute right-4 top-4 rounded-full bg-white/10 p-2 text-white hover:bg-white/20"
              aria-label="Close preview"
            >
              <X className="h-5 w-5" />
            </button>
            <img
              src={url}
              alt={alt}
              onClick={(e) => e.stopPropagation()}
              className="max-h-[85vh] max-w-[90vw] rounded-lg object-contain shadow-2xl"
            />
          </div>,
          document.body
        )}
    </>
  );
}
